/**
 * The boss, big, with the two numbers that matter.
 *
 * Shared by the create screen and the lobby so a host sees the same thing while
 * setting a raid up as everyone sees once it is live — and so the 100% CPs are
 * rendered by ONE piece of code. Two implementations of "show the hundo CP"
 * would eventually disagree, and a wrong hundo CP is not a cosmetic bug: someone
 * reads it, their encounter says something else, and they transfer the Pokémon.
 *
 * A MAX BATTLE HAS ONE NUMBER. Weather does not boost a Power Spot encounter, so
 * `hundoCp.boosted` is null for a Dynamax boss — absent, not unknown — and the
 * boosted figure is not drawn at all rather than drawn empty.
 */
import { CaughtSprite } from "@/components/CaughtSprite";
import { raidBossArtCandidates, raidBossSilhouetteSlug } from "@/lib/formSpriteCandidates";
import { raidBossArtRef } from "@/lib/raidHubClient";
import type { RaidBossRef, RaidBossTier } from "@/lib/raidHubClient";
import { CloudSun, Sparkles, Sun } from "lucide-react";

export function tierLabel(tier: RaidBossTier): string {
  switch (tier) {
    case "1": return "1★";
    case "3": return "3★";
    case "5": return "5★";
    case "mega": return "Mega";
    case "max": return "Max Battle";
  }
}

export function RaidBossHeadline({
  boss,
  size = 96,
}: {
  boss: RaidBossRef;
  size?: number;
}) {
  const artRef = raidBossArtRef(boss);
  return (
    <div className="flex flex-col items-center text-center" data-testid="raid-boss-headline">
      {artRef && (
        <CaughtSprite
          id={artRef.speciesId}
          size={size}
          shiny={false}
          ariaLabel={boss.name}
          formSpriteCandidates={raidBossArtCandidates(artRef)}
          formSlug={raidBossSilhouetteSlug(artRef)}
        />
      )}
      <h2 className="mt-1 text-xl font-semibold leading-tight" data-testid="raid-boss-name">
        {boss.name}
      </h2>
      <div className="mt-1 flex items-center justify-center gap-1.5">
        <span
          className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
          style={{ background: "var(--surface-sunken)", color: "var(--text-secondary)" }}
          data-testid="raid-boss-tier"
        >
          {tierLabel(boss.tier)}
        </span>
        {boss.canBeShiny && (
          <Sparkles
            size={13}
            className="text-[hsl(var(--shiny,43_96%_60%))]"
            aria-label="Can be shiny"
          />
        )}
      </div>

      {boss.hundoCp && (
        <div className="mt-3 w-full" data-testid="raid-boss-hundo">
          <p
            className="text-[9px] font-bold uppercase tracking-[0.16em] mb-1"
            style={{ color: "var(--text-tertiary)" }}
          >
            100% catch CP
          </p>
          <div className="flex items-stretch justify-center gap-2">
            <HundoCp
              icon={<Sun size={11} aria-hidden />}
              label="Normal"
              value={boss.hundoCp.normal}
              testId="raid-hundo-normal"
            />
            {/* Drawn only when the encounter can actually be boosted. */}
            {boss.hundoCp.boosted != null && (
              <HundoCp
                icon={<CloudSun size={11} aria-hidden />}
                label="Weather"
                value={boss.hundoCp.boosted}
                testId="raid-hundo-boosted"
                accent
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HundoCp({
  icon,
  label,
  value,
  testId,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  testId: string;
  accent?: boolean;
}) {
  const color = accent ? "hsl(207 90% 60%)" : "var(--text-primary)";
  return (
    <div
      className="flex-1 max-w-[128px] rounded-xl px-2 py-1.5"
      style={{
        background: "var(--surface-sunken)",
        border: `1px solid ${accent ? "hsl(207 90% 60% / 0.35)" : "var(--border-subtle)"}`,
      }}
      data-testid={testId}
    >
      <p
        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider"
        style={{ color: "var(--text-tertiary)" }}
      >
        {icon}
        {label}
      </p>
      <p className="text-base font-bold tabular-nums leading-tight" style={{ color }}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}
